import React, { useEffect, useState } from "react";
import { Sparkles, Eye, Flame, Rocket } from "lucide-react";
import ProductCard from "./ProductCard";
import { api } from "../lib/api";
import { getVisitorId } from "../lib/visitor";
import { useAuth } from "../context/AuthContext";

const REASON_COPY = {
  viewed: {
    kicker: "// PICKED UP WHERE YOU LEFT OFF",
    title: <>Recommended <span className="text-[#CFF048]">for you.</span></>,
    Icon: Eye,
    sub: "Tees you clicked into but didn't cop yet. Grab them before they drop out.",
  },
  bestsellers: {
    kicker: "// THE CULT IS COPPING THESE",
    title: <>Bestsellers <span className="text-[#CFF048]">right now.</span></>,
    Icon: Flame,
    sub: "Flying off the rack this week. Handpicked from paid orders.",
  },
  "cold-start": {
    kicker: "// FRESH OFF THE PRESS",
    title: <>New in the <span className="text-[#CFF048]">cult.</span></>,
    Icon: Rocket,
    sub: "First-time here? Start with what dropped last. Limited runs, once they go — they're gone.",
  },
};

export default function RecommendedStrip() {
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("cold-start");
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const vid = getVisitorId();
    api.get(`/products/recommendations?limit=4&visitor_id=${encodeURIComponent(vid)}`)
      .then((r) => {
        setItems(r.data?.items || []);
        setReason(r.data?.reason || "cold-start");
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Re-fetch when auth state flips (login/logout) so personalisation switches sources
  }, [user?.id]);

  if (loading || items.length === 0) return null;

  const copy = REASON_COPY[reason] || REASON_COPY["cold-start"];
  const Icon = copy.Icon;

  return (
    <section
      className="relative border border-[#CFF048]/40 bg-gradient-to-br from-[#0A0A0A] via-black to-[#0A0A0A] p-5 md:p-7 mb-10 overflow-hidden"
      data-testid="recommended-strip"
    >
      {/* subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#CFF048 1px, transparent 1px)", backgroundSize: "22px 22px" }}
      />
      <div className="relative">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
          <div>
            <p className="label-mono text-[#CFF048] flex items-center gap-2">
              <Icon size={13} /> {copy.kicker}
            </p>
            <h2 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight mt-1 leading-none">
              {copy.title}
            </h2>
            <p className="text-[#A1A1AA] text-sm mt-2 max-w-xl">{copy.sub}</p>
          </div>
          <div className="hidden md:flex items-center gap-2 label-mono text-[10px] text-[#71717A] tracking-widest">
            <Sparkles size={12} className="text-[#CFF048]" /> AI-PICKED · UPDATES AS YOU BROWSE
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((p, i) => (
            <div key={p.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <ProductCard p={p} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
